import { Dashboard, TabGroup } from "api/model/dashboard/Dashboard";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { T } from "services/lang/LanguageService";

interface DashboardTabsDropdownMenuProps {
  isMobile: boolean;
  menuRef: React.RefObject<Menu | null>;
  tabId: number;
  dashboard: Dashboard;
}

const getMenuItems = (
  tabs: DashboardTabModel[],
  tabGroups: TabGroup[],
  onTabChange: (uuid: string) => void
): MenuItem[] => {
  if (tabGroups.length === 0) {
    return tabs.map(tab => ({
      label: tab.title,
      command: () => onTabChange(tab.uuid),
    }));
  }

  const menuItems: MenuItem[] = tabGroups.map(group => ({
    label: group.title,
    items: tabs
      .filter(tab => tab.tabGroupId === group.uuid)
      .map(tab => ({
        label: `\u00A0\u00A0 ${tab.title}`,
        command: () => onTabChange(tab.uuid),
      })),
  }));

  const ungroupedTabs = tabs.filter(tab => !tab.tabGroupId);
  if (ungroupedTabs.length > 0) {
    menuItems.push({
      label: T("label.tabs-without-group"),
      items: ungroupedTabs.map(tab => ({
        label: `\u00A0\u00A0 ${tab.title}`,
        command: () => onTabChange(tab.uuid),
      })),
    });
  }

  return menuItems;
};

const getDropdownOptions = (tabs: DashboardTabModel[], tabGroups: TabGroup[] = []) => {
  if (tabGroups.length === 0) {
    return tabs.map(tab => ({
      label: tab.title,
      value: tab.uuid,
    }));
  }

  return tabGroups
    .map(group => ({
      label: group.title,
      code: group.uuid,
      items: tabs
        .filter(tab => tab.tabGroupId === group.uuid)
        .map(tab => ({
          label: `\u00A0\u00A0 ${tab.title}`,
          value: tab.uuid,
        })),
    }))
    .concat(
      tabs.filter(tab => !tab.tabGroupId).length > 0
        ? [
            {
              label: T("label.tabs-without-group"),
              code: "ungrouped",
              items: tabs
                .filter(tab => !tab.tabGroupId)
                .map(tab => ({
                  label: `\u00A0\u00A0 ${tab.title}`,
                  value: tab.uuid,
                })),
            },
          ]
        : []
    );
};

const DashboardTabsDropdownMenu = ({
  isMobile,
  menuRef,
  tabId,
  dashboard,
}: DashboardTabsDropdownMenuProps) => {
  const navigate = useNavigate();

  const selectedTabUUID = useMemo(() => {
    const currentTab = dashboard?.dashboardTabsJs?.[tabId];
    return currentTab ? currentTab.uuid : null;
  }, [dashboard?.dashboardTabsJs, tabId]);

  const handleTabChange = (tabUUID: string, e?: DropdownChangeEvent) => {
    const selectedIndex = dashboard.dashboardTabsJs.findIndex(tab => tab.uuid === tabUUID);

    const isCtrlOrMetaPressed =
      e?.originalEvent &&
      ("ctrlKey" in e.originalEvent || "metaKey" in e.originalEvent) &&
      ((e.originalEvent as unknown as MouseEvent).ctrlKey ||
        (e.originalEvent as unknown as MouseEvent).metaKey);

    if (!isMobile && isCtrlOrMetaPressed) {
      const newTabUrl = `${window.location.pathname}/#/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      window.open(newTabUrl, "_blank");
    } else {
      const url = `/dashboard/${dashboard.id}/tab/${selectedIndex}`;
      navigate(url);
    }
  };

  return (
    <div>
      {isMobile ? (
        <Menu
          ref={menuRef}
          model={getMenuItems(
            dashboard.dashboardTabsJs,
            dashboard.tabGroups ?? [],
            handleTabChange
          )}
          popup
          className="w-12 menu-list"
        />
      ) : (
        <Dropdown
          value={selectedTabUUID}
          options={getDropdownOptions(dashboard.dashboardTabsJs, dashboard.tabGroups ?? [])}
          optionLabel="label"
          optionGroupLabel={(dashboard.tabGroups ?? []).length > 0 ? "label" : undefined}
          optionGroupChildren="items"
          onChange={e => handleTabChange(e.value, e)}
          className="w-20rem"
          filter
        />
      )}
    </div>
  );
};

export default DashboardTabsDropdownMenu;
